/**
 * Quality tiers. Ordered so that each step down sheds debris first, then
 * fluid grid resolution, then render scale. The governor only moves between
 * adjacent tiers; this table encodes the downgrade order.
 */

export type TierId = 'low' | 'medium' | 'high' | 'ultra';

export interface QualityTier {
  id: TierId;
  maxDebris: number;
  /** N for the N x N stable-fluids grid (phase 6). */
  fluidGridSize: number;
  /** Fraction of devicePixelRatio. */
  renderScale: number;
  cloudMode: 'billboards' | 'raymarch-quarter' | 'raymarch-half';
  cloudSteps: number;
  cowLod: 0 | 1 | 2;
}

// Checked 2026-10-21 against bench/calibration/ (dev Mac, Inspiron 7405, iPhone 14 Pro Max):
// high held 60 fps on all three; ultra held 60 fps on two and dropped to ~30 fps on the
// Inspiron. No device has measured the low/medium boundary yet.
// TODO(calibrate): run the sweep on a weak device before tuning low and medium.
export const TIERS: Readonly<Record<TierId, Readonly<QualityTier>>> = {
  low: {
    id: 'low',
    maxDebris: 2_000,
    fluidGridSize: 64,
    renderScale: 0.5,
    cloudMode: 'billboards',
    cloudSteps: 0,
    cowLod: 2,
  },
  medium: {
    id: 'medium',
    maxDebris: 10_000,
    fluidGridSize: 128,
    renderScale: 0.75,
    cloudMode: 'raymarch-quarter',
    cloudSteps: 24,
    cowLod: 1,
  },
  high: {
    id: 'high',
    maxDebris: 50_000,
    fluidGridSize: 256,
    renderScale: 1.0,
    cloudMode: 'raymarch-half',
    cloudSteps: 48,
    cowLod: 0,
  },
  ultra: {
    id: 'ultra',
    maxDebris: 200_000,
    fluidGridSize: 256,
    renderScale: 1.0,
    cloudMode: 'raymarch-half',
    cloudSteps: 64,
    cowLod: 0,
  },
};

/** Lowest to highest. Index arithmetic on this array is how tiers step. */
export const TIER_ORDER: readonly TierId[] = ['low', 'medium', 'high', 'ultra'];

/** Largest debris count across all tiers. CPU tracer buffers are sized to this. */
export const MAX_DEBRIS_ANY_TIER = TIERS.ultra.maxDebris;

export function tierIndex(id: TierId): number {
  return TIER_ORDER.indexOf(id);
}
